import { ReactNode } from 'react';

type ChildrenFunction<T, P> = P extends undefined
  ? (value: NonNullable<T>) => ReactNode
  : (params: NonNullable<P>) => ReactNode;

interface Props<T, P = undefined> {
  when: T;
  params?: P;
  children: ReactNode | ChildrenFunction<T, P>;
}
/**
 * 조건부로 children을 렌더링하는 컴포넌트
 *
 * @param props.when - 렌더링 여부를 결정하는 조건
 * @param props.params - params가 존재하면 자식 함수의 인자는 props.when이 아닌 props.params으로 사용됨.
 * @param props.children - 렌더링할 컨텐츠 또는 함수
 * @returns 조건에 따라 렌더링된 컨텐츠
 *
 * @example
 * // 조건부 렌더링
 * <Show when={isLoggedIn}>
 *   <WelcomeMessage />
 * </Show>
 *
 * @example
 * // when으로 사용한 값을 children에서 사용할 때
 * <Show when={item}>
 *   {(item) => <ItemRenderer item={item} />} // item is NonNullable<T>
 * </Show>
 *
 * @example
 * // 타입이 보장된 데이터를 children에서 사용해야할 때 render prop으로 사용
 * <Show when={isLoggedIn} params={user}>
 *   {(user) => <UserProfile user={user} />}
 * </Show>
 *
 * @example
 * // 조건이 falsy일 때
 * <Show when={false}>
 *   <div>children은 렌더링되지 않음</div>
 * </Show>
 */
function Show<T, P = undefined>({ when, params, children }: Props<T, P>) {
  if (!when) {
    return null;
  }

  if (typeof children === 'function') {
    return typeof params !== 'undefined' ? (
      <>{(children as (params: P) => ReactNode)(params)}</>
    ) : (
      <>{(children as (value: NonNullable<T>) => ReactNode)(when as NonNullable<T>)}</>
    );
  }

  return <>{children}</>;
}

export default Show;
